import {
	DeleteProcessInstance,
	GetWorkflowHistoryByCriteriaEndpoint,
	GetTaskVariables,
	cloneWorkflow as cloneWorkflowEndpoint,
	PostregeneratePDF,
	GetFormByUserTaskEndPoint,
	setAssignEndPoint,
	PutProcessInstanceVariableEndPoint,
	GetWorkflowTasksPendingEndPoint,
	SearchWorkflowTasksPendingEndPoint,
	ExecuteWorkflowTaskEndPoint,
	GetTaskHistoryDataEndPoint,
	UnclaimWorkflowTaskEndPoint,
	DeleteWorkflowProcessEndPoint,
	ProcessDocumentsEndPoint,
	ProcessDocumentDownloadEndPoint,
	ClaimTaskEndPoint,
	GetWorkflowsEndpoint,
	StartProcessByDefinitionKeyEndPoint,
	StartProcessEndPoint,
	StartProcessByKeyEndPoint,
	GetAllEndPoint
} from "common/constants";
import { makeAuthenticatedRequest } from "../helpers/authenticatedRequest";
import { getFormByID } from "../api/formsApi"
import { getAllDatabaseRecord } from "./databaseApi";

export const GetWorkflowHistoryFromSearchIndex = async (
	assignee,
	{
		StartDateTime = null,
		EndDateTime = null,
		SearchParam = "",
		SortField = "modified_at",
		SortDirection = "desc",
		RecordsByPage = 10,
		PageNumber = 1,
		Lang = "en",
	} = {},
	signal = null
) => {
	return await makeAuthenticatedRequest(
		`${GetWorkflowHistoryByCriteriaEndpoint}/${assignee}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				StartDateTime,
				EndDateTime,
				SearchParam,
				SortField,
				SortDirection,
				RecordsByPage,
				PageNumber,
				Lang,
			}),
			signal,
		}
	);
};

export const deletePendingTask = async ({ task_id }, signal = null) => {
	const url = `${DeleteProcessInstance}/${task_id}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "DELETE",
		signal,
	});

	return response;
};

// Documents generated so far for a process (any status). The backend checks download_pdf on the
// process's workflow in the company.
export const getProcessDocuments = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ProcessDocumentsEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};

// A short-lived (60 s) signed link to download one document from the private bucket, after the
// same access check. Use it right away; don't store it.
export const getProcessDocumentDownload = async ({ documentId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ProcessDocumentDownloadEndPoint, {
		method: "POST",
		body: JSON.stringify({ documentId, companyId }),
		signal,
	});

	return response;
};

// Deletes (cancels) a whole process. The backend decides who may, from the caller (token), the
// workflow's delete_workflow permission in the company and its delete policy, so only the process
// and the company are sent.
export const deleteWorkflowProcess = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(DeleteWorkflowProcessEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};

export const getWorkflowsList = async (userId, companyId, signal = null) => {
	const url = `${GetWorkflowsEndpoint}?userId=${userId}&companyId=${companyId}`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

import { parseAnyDate, getRecordDate } from "../helpers/dateUtils";
export { parseAnyDate, getRecordDate };

export const getWorkflowHistories = async (
	tableName = "tbl_workflow_instances",
	assignee,
	signal = null
) => {
	if (!assignee) {
		return { data: { records: [], totalCount: 0 } };
	}

	// 1. Fetch user's tasks from tbl_workflow_tasks to find related process_instance_ids
	const tasksResponse = await getAllDatabaseRecord(
		{
			tableName: "tbl_workflow_tasks",
			condition: `assignee=eq.${assignee}`,
			columns: "process_instance_id",
		},
		signal
	);

	const taskRecords = Array.isArray(tasksResponse?.data)
		? tasksResponse.data
		: Array.isArray(tasksResponse?.data?.records)
			? tasksResponse.data.records
			: [];

	const processInstanceIds = Array.from(
		new Set(taskRecords.map((r) => r.process_instance_id).filter(Boolean))
	);

	if (processInstanceIds.length === 0) {
		return { data: { records: [], totalCount: 0 } };
	}

	// 2. Fetch workflow instances matching those process_instance_ids
	const condition =
		processInstanceIds.length === 1
			? `process_instance_id=eq.${processInstanceIds[0]}`
			: `process_instance_id=in.(${processInstanceIds.join(",")})`;

	const instancesResponse = await getAllDatabaseRecord(
		{
			tableName: tableName || "tbl_workflow_instances",
			condition,
		},
		signal
	);

	const instanceRecords = Array.isArray(instancesResponse?.data)
		? instancesResponse.data
		: Array.isArray(instancesResponse?.data?.records)
			? instancesResponse.data.records
			: [];

	// Robust in-memory sort descending by modified_at or started_at or created_at
	instanceRecords.sort((a, b) => getRecordDate(b) - getRecordDate(a));

	return {
		data: {
			records: instanceRecords,
			totalCount: instanceRecords.length,
		},
	};
};

export const getTaskVariables = async (processInstanceId, signal = null) => {
	const url = `${GetTaskVariables}/${processInstanceId}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "GET",
		signal,
	});

	return response;
};

export const cloneWorkflow = async (applicationId, assignee, processInstanceId = "", signal = null) => {
	const url = `${cloneWorkflowEndpoint}/${applicationId}/${assignee}/${processInstanceId}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "POST",
		signal,
	});

	return response;
};

export const postRegeneratePDF = async (applicationID, templateId, tableId, signal = null) => {
	const url = `${PostregeneratePDF}/${applicationID}/${templateId}/${tableId}`;
	const response = await makeAuthenticatedRequest(url, { method: "POST", signal });
	return response;
};

export const getFormByUserTask = async (processInstanceId, signal = null) => {
	const url = `${GetFormByUserTaskEndPoint}/${processInstanceId}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "GET",
		signal,
	});

	return response;
};

export const setAssigneeForTask = async (assigneeData, signal = null) => {
	const { taskId, userId, tableName, fieldName, createdId, processId, userInfo, driverInfo, selectedCompany } = assigneeData;
	const jsonData = {
		"userID": userId,
		"databaseSync": {
			"tableName": tableName,
			"fieldName": fieldName,
			"createdId": createdId
		},
		"notification": {
			"processId": processId,
			"loggedInUserFullName": userInfo.loggedInUserFullName,
			"oldDriverFullName": driverInfo.oldDriver.fullName,
			"oldDriverPhoneNumber": driverInfo.oldDriver.phoneNumber,
			"newDriverFullName": driverInfo.newDriver.fullName,
			"newDriverPhoneNumber": driverInfo.newDriver.phoneNumber,
			"selectedCompany": selectedCompany
		}
	}
	const url = `${setAssignEndPoint}/${taskId}`;

	const response = await makeAuthenticatedRequest(url,
		{
			method: "POST",
			body: JSON.stringify(jsonData),
			signal,
		}
	);

	return response;
};

export const putProcessInstanceVariable = async (processInstanceId, variableName, jsonData, signal = null) => {
	const url = `${PutProcessInstanceVariableEndPoint}/${processInstanceId}/${variableName}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "PUT",
		body: JSON.stringify(jsonData),
		signal,
	});

	return response;
};

// request: a PendingTasksRequest (dto/workflowDto) plus companyId (added by the saga); the search
// endpoint also gets lang. The backend always uses the logged-in user (token) as the assignee.
// Plain list: raw-column sorts, no search text.
export const getPendingTasks = async (request, signal = null) => {
	const result = await makeAuthenticatedRequest(GetWorkflowTasksPendingEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});

	return result;
};

// Same request, through the search RPC: search text and sorting by translated label.
export const searchPendingTasks = async (request, signal = null) => {
	const result = await makeAuthenticatedRequest(SearchWorkflowTasksPendingEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});

	return result;
};

// The backend checks the caller is the assignee (from the token) and may unclaim in the company,
// so only the task and the company are sent.
export const unclaimPendingTask = async ({ taskId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(UnclaimWorkflowTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ taskId, companyId }),
		signal,
	});

	return response;
};

export const claimTask = async (taskID, userID, signal = null) => {
	var endpoint = `${ClaimTaskEndPoint}/${taskID}/${userID}`;

	const response = await makeAuthenticatedRequest(endpoint, {
		method: "POST",
		signal,
	});

	return response;
};

export const startWorkflowProcess = async (definitionKey, assignee, signal = null) => {
	var endpoint = `${StartProcessEndPoint}/${definitionKey}/${assignee}`;

	const response = await makeAuthenticatedRequest(endpoint, {
		method: "POST",
		signal,
	});

	return response;
};

// Starts the latest deployed version of a process definition key.
export const startWorkflowProcessByKey = async (definitionKey, assignee, signal = null) => {
	const endpoint = `${StartProcessByKeyEndPoint}/${encodeURIComponent(definitionKey)}/${encodeURIComponent(assignee)}`;

	const response = await makeAuthenticatedRequest(endpoint, {
		method: "POST",
		signal,
	});

	return response;
};

// The backend finds the caller's open task and resolves their roles/forms in the company itself,
// so only the process and the company are sent (no task id, no roles).
export const executePendingTask = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ExecuteWorkflowTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};

export const getTaskHistoryData = async ({ processInstanceId, roleIds }, signal = null) => {
	const response = await makeAuthenticatedRequest(GetTaskHistoryDataEndPoint, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			processInstanceId,
			roleIds: Array.isArray(roleIds) ? roleIds : [roleIds].filter(Boolean),
		}),
		signal,
	});

	return response;
};

